import { container } from '@/app-modules/_ioc';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { UserPreferences, UserWindowPreference, WindowType, IWindowLaunchCondition } from '@/app-modules/store/modules/user-preferences';
import { IModal } from '@/plugins/modal-manager/modal';
import { IModalLayoutStorageHandler, ModalLayoutStorageHandler, NullModalLayoutStorageHandler } from '@/plugins/modal-manager/modal-layout-storage-handler';
import { IModalSpawnAgent } from '@/plugins/modal-manager/modal-spawn-agent';
import { ShortcutHint } from '@/components/home/_components/tombola-shortcut-overlay/_domain/models/shortcut-hint';
import { ContextMenuConfiguration, IContextMenuOption, IContextMenuHeader } from '@/app-modules/tombola-context-menu';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { IModalLaunchOptions } from '@/plugins/modal-manager/models';
import { compareGameNames } from '@/components/game/_domain';
import { IGameChatRoomViewModel } from '@/app-modules/config';

const MODAL_ARRANGEMENT_STEP_PIXELS = 32;
const MAX_NUMBER_OF_MODAL_PLACEMENT_COLUMNS = 3;
const MAX_NUMBER_OF_MODAL_PLACEMENT_ROWS = 2;
const MAX_NUMBER_OF_TILED_MODALS = MAX_NUMBER_OF_MODAL_PLACEMENT_COLUMNS * MAX_NUMBER_OF_MODAL_PLACEMENT_ROWS;

// tslint:disable-next-line:interface-name
interface ModalTilingLayout {
  numberOfModals: number;
  numberOfRows: number;
  numberOfColumns: number;
}

class ModalManagerPlugin {
  private modals: IModal[] = [];
  private shortcutHints: ShortcutHint[] = [];
  private readonly defaultModalLayoutStorageHandler = new ModalLayoutStorageHandler();
  private readonly nullModalLayoutStorageHandler = new NullModalLayoutStorageHandler();
  private modalSpawnAgentInstance: IModalSpawnAgent;
  private showShortcutOverlay: boolean = false;

  public get spawnAgent(): IModalSpawnAgent {
    if (!this.modalSpawnAgentInstance) {
      this.modalSpawnAgentInstance = container.get(SERVICE_IDENTIFIERS.IModalSpawnAgent);
    }
    return this.modalSpawnAgentInstance;
  }

  public get shouldShowShortcutOverlay(): boolean {
    return this.showShortcutOverlay;
  }

  public get modalShortcutHints(): ShortcutHint[] {
    return this.shortcutHints;
  }

  public clearShortcutHints() {
    this.shortcutHints = [];
  }

  public isModalRegistered(uId: string): boolean {
    return this.modals.some((modal) => modal.modalGuid === uId);
  }

  public registerModal(modal: IModal) {
    this.populateShortcutIdForModal(modal);
    this.modals.push(modal);
    this.bringToFront(modal.modalGuid);
    this.storeUserWindowPreferenceForModal(modal);
  }

  public deregisterModal(modal: IModal) {
    const index = this.modals.findIndex((item) => item.modalGuid === modal.modalGuid);
    const hintIndex = this.modalShortcutHints.findIndex((item) => item.modalGuid === modal.modalGuid);
    if (index !== -1) {
      this.modals.splice(index, 1);
      this.disableAutoRestoreUserWindowPreferenceForModal(modal);
    }
    if (hintIndex !== -1) {
      this.modalShortcutHints.splice(hintIndex, 1);
    }

    const sortedModals = this.sortModalListOnZIndex(this.modals);
    if (sortedModals.length > 0) {
      this.bringToFront(sortedModals[sortedModals.length - 1].modalGuid);
    }
  }

  public bringToFront(modalGuid: string) {
    const sortedModals = this.sortModalListOnZIndex(this.modals);

    let z: number = 0;
    sortedModals.forEach((item) => {
      if (item.modalGuid !== modalGuid) {
        this.orderModalOnScreen(item, (100 + z++));
      } else {
        this.placeModalToFrontOfScreen(item);
      }
    });
  }

  public refocusLastModal() {
    const modalsList = this.getAllModalsForArrangeAndTile();
    if (modalsList.length > 0) {
      this.placeModalToFrontOfScreen(modalsList[modalsList.length - 1]);
    }
  }

  public arrangeAllModals() {
    const modalsList = this.getAllModalsForArrangeAndTile();
    this.arrangeModals(modalsList);
  }

  private arrangeModals(modalsList: IModal[], startingPositionInPixels?: { left: number, top: number }) {
    const newPositionInPixels = startingPositionInPixels || { left: 0, top: 0 };

    for (const modal of modalsList) {
      modal.moveTo(newPositionInPixels);
      newPositionInPixels.left += MODAL_ARRANGEMENT_STEP_PIXELS;
      newPositionInPixels.top += MODAL_ARRANGEMENT_STEP_PIXELS;
    }
  }

  public tileAllModals() {
    const workbenchDimensionsInPixels = this.getWorkbenchDimensionsInPixels();
    const modalsList: IModal[] = this.getAllModalsForArrangeAndTile();
    const numberOfModals = modalsList.length;
    const normalisedNumberOfModals = numberOfModals <= MAX_NUMBER_OF_TILED_MODALS ? numberOfModals : MAX_NUMBER_OF_TILED_MODALS;
    const modalTilingLayout = this.getModalTilingLayout(numberOfModals);
    const placeModalsOnNextRowAfterModalIndex = Math.ceil(normalisedNumberOfModals / modalTilingLayout.numberOfRows);

    const newPositionInPixels = { left: 0, top: 0 };
    const modalDimensions = {
      width: workbenchDimensionsInPixels.width / modalTilingLayout.numberOfColumns,
      height: workbenchDimensionsInPixels.height / modalTilingLayout.numberOfRows
    };
    modalsList.slice(0, MAX_NUMBER_OF_TILED_MODALS).forEach((modal, modalIndex) => {
      modal.moveTo(newPositionInPixels);
      modal.resize(modalDimensions);
      if (modalIndex > 0 && (modalIndex + 1) === placeModalsOnNextRowAfterModalIndex) {
        newPositionInPixels.left = 0;
        newPositionInPixels.top += modalDimensions.height;

        if (normalisedNumberOfModals % 2 === 1) {
          modalDimensions.width = workbenchDimensionsInPixels.width / 2;
        }
      }
      else {
        newPositionInPixels.left += modalDimensions.width;
      }
    });

    const startingPositionInPixels = { left: MODAL_ARRANGEMENT_STEP_PIXELS, top: MODAL_ARRANGEMENT_STEP_PIXELS };
    this.arrangeModals(modalsList.slice(MAX_NUMBER_OF_TILED_MODALS), startingPositionInPixels);
  }

  public allModalsMinimised(): boolean {
    return this.modals.every((modal) => modal.isMinimised);
  }

  public minimiseAllModals() {
    this.modals.forEach((modal) => modal.minimise());
  }

  public restoreAllModals() {
    this.modals.forEach((modal) => modal.restore());
  }

  public adjustModalsAfterPinningNavigationMenu() {
    const modalsList = this.getAllModalsForArrangeAndTile();

    for (const modal of modalsList) {
      const navigationMenuOffsetWhenPinned = 80;
      const modalLocationAndDimensions = modal.getLocationAndDimensionsInPixels();
      const modalLocationRight = modalLocationAndDimensions.left + modalLocationAndDimensions.width + navigationMenuOffsetWhenPinned;

      if (modalLocationRight > window.innerWidth) {
        const pixelsToMove = modalLocationRight - window.innerWidth;
        modalLocationAndDimensions.left -= pixelsToMove;
        modal.moveTo(modalLocationAndDimensions);
        this.storeUserWindowPreferenceForModal(modal);
      }
    }
  }

  public destroyAllModals() {
    this.modals.slice(0).forEach((modal) => modal.$destroy());
    this.modals.splice(0);
  }

  public showModalShortcutIcons() {
    this.showShortcutOverlay = true;
  }

  public hideModalShortcutIcons() {
    this.showShortcutOverlay = false;
  }

  public getShortcutHintByShortcutId(shortcutId: number): ShortcutHint {
    return this.shortcutHints.find((shortcut) => shortcut.shortcutId === shortcutId);
  }

  public getModal(modalGuid: string): IModal {
    return this.modals.find((modal) => modal.modalGuid === modalGuid);
  }

  public buildRoomSwapMenuConfigurationForModal(modal: IModal): ContextMenuConfiguration {
    const chatModals = this.getRoomSwapModals(modal);
    const menuHeader = this.getRoomSwapContextMenuHeader();
    const menuOptions = this.getRoomSwapContextMenuOptions(modal, chatModals);
    return new ContextMenuConfiguration(menuHeader, menuOptions);
  }

  public disableAutoRestoreUserPreferenceForAllModals(): void {
    const handler = this.getModalLayoutPersistHandler();
    handler.disableAutoRestoreUserWindowPreferenceForAllModals();
  }

  public getActiveModal(): IModal {
    return this.modals.find((x) => x.isActive);
  }

  private getRoomSwapModals(modal: IModal): IModal[] {
    return this.modals.filter((o) => o.modalType === WindowType.Chat && o.modalGuid !== modal.modalGuid);
  }

  private getRoomSwapContextMenuHeader(): IContextMenuHeader {
    const localisationService: ILocalisationService = container.get(SERVICE_IDENTIFIERS.ILocalisationService);
    const $lang = localisationService.getLangInstance();
    return {
      headerText: $lang.chat.roomSwap.menuTitle,
      iconCssClass: 'fa-exchange'
    };
  }

  private getRoomSwapContextMenuOptions(modal: IModal, chatModals: IModal[]): IContextMenuOption[] {
    const self = this;
    return chatModals.map((o) => ({
      optionText: o.modalTitleText,
      iconCssClass: 'fa-comments',
      menuOptionSelectedCallback: () => {
        self.swapModals(modal, o);
        self.bringToFront(o.modalGuid);
      }
    }));
  }

  private swapModals(modalA: IModal, modalB: IModal): void {
    if (modalA && modalB) {
      const locationAndDimensionsA = modalA.getLocationAndDimensionsInPixels();
      const locationAndDimensionsB = modalB.getLocationAndDimensionsInPixels();
      modalA.moveTo(locationAndDimensionsB);
      modalA.resize(locationAndDimensionsB);
      modalB.moveTo(locationAndDimensionsA);
      modalB.resize(locationAndDimensionsA);
    }
  }

  private populateShortcutIdForModal(modal: IModal): void {
    if (modal.modalType === WindowType.Chat) {
      let shortcutId = 0;
      if (modal.modalProps.gameName === 'cmchat') {
        shortcutId = 1;
      } else {
        const currentShortcutIds = this.getSortedShortcutIdsForCurrentModals();
        shortcutId = this.getFirstUnusedNumberInSequence(currentShortcutIds, 1);
      }
      this.shortcutHints.push(new ShortcutHint(shortcutId, modal.modalGuid));
    }
  }

  private getSortedShortcutIdsForCurrentModals(): number[] {
    return this.shortcutHints
      .map((m) => m.shortcutId)
      .sort(this.bubbleNumberSort);
  }

  private getFirstUnusedNumberInSequence(sequence: number[], min: number): number {
    const sequenceLength = sequence.length;
    let offset = sequence[0];
    let lowestUnusedNumber = -1;
    for (let index = 0; index < sequenceLength; ++index) {
      const currentNumber = sequence[index];
      if (currentNumber !== offset) {
        lowestUnusedNumber = offset;
        break;
      }
      ++offset;
    }

    if (lowestUnusedNumber === -1) {
      lowestUnusedNumber = sequenceLength ? (sequence[sequenceLength - 1] + 1) : (min + 1);
    }
    return lowestUnusedNumber;
  }

  private getModalTilingLayout(numberOfModals: number): ModalTilingLayout {
    const numberOfColumns = this.calcNumberOfColumnsForModalTilingLayout(numberOfModals);
    const numberOfRows = this.calcNumberOfRowsForModalTilingLayout(numberOfModals, numberOfColumns);
    return { numberOfModals, numberOfColumns, numberOfRows };
  }

  private calcNumberOfColumnsForModalTilingLayout(numberOfModals: number): number {
    if (numberOfModals < MAX_NUMBER_OF_MODAL_PLACEMENT_COLUMNS) {
      return numberOfModals;
    }

    if (numberOfModals % (MAX_NUMBER_OF_MODAL_PLACEMENT_COLUMNS + 1) === 0) {
      return Math.ceil(numberOfModals / MAX_NUMBER_OF_MODAL_PLACEMENT_COLUMNS);
    }

    return MAX_NUMBER_OF_MODAL_PLACEMENT_COLUMNS;
  }

  private calcNumberOfRowsForModalTilingLayout(numberOfModals: number, numberOfColumns: number): number {
    const numberOfRows = 1 + Math.ceil((numberOfModals - numberOfColumns) / MAX_NUMBER_OF_MODAL_PLACEMENT_ROWS);
    if (numberOfRows < MAX_NUMBER_OF_MODAL_PLACEMENT_ROWS) {
     return numberOfRows;
    }
    return MAX_NUMBER_OF_MODAL_PLACEMENT_ROWS;
  }

  private getAllModalsForArrangeAndTile(): IModal[] {
    const modals = this.modals.filter((modal) => !modal.isMinimised);
    return [].concat(...modals.filter((modal) => !modal.isActive))
             .concat(...modals.filter((modal) => modal.isActive));
  }

  private getWorkbenchDimensionsInPixels() {
    const workbenchElement: HTMLElement = document.querySelector('#workbench');
    return {
      width: workbenchElement.clientWidth,
      height: workbenchElement.clientHeight
    };
  }

  private orderModalOnScreen(item: IModal, order: number) {
    item.$el.style.zIndex = order.toString();
    item.isActive = false;
  }

  private placeModalToFrontOfScreen(item: IModal) {
    item.$el.style.zIndex = (999).toString();
    item.isActive = true;
    this.restoreSelectedModal(item.modalId);

    const chatTextEntry = item.$el.getElementsByClassName('chat-text-entry');
    if (chatTextEntry.length === 1) {
      chatTextEntry.item(0).getElementsByTagName('input').item(0).focus();
    }

    // When we bring to front it could be a minimised modal so lets remove it
    // TODO: TECH DEBT - Need to do this check and remove here because minimised-modal.component.ts doesn't have Uid.
    if (item.minimisedModal !== null) {
      item.minimisedModal.$el.remove();
      item.minimisedModal = null;
    }
  }

  private sortModalListOnZIndex(modals: IModal[]): IModal[] {
    return modals.sort((item1, item2) => this.bubbleNumberSort(+item1.$el.style.zIndex, +item2.$el.style.zIndex));
  }

  private bubbleNumberSort(item1: number, item2: number): number {
    if (item1 < item2) {
      return -1;
    } else if (item2 < item1) {
      return 1;
    }
    return 0;
  }

  public restoreSelectedModal(modalId: string) {
    const modal = this.modals.find((o) => o.modalId === modalId);
    if (modal) {
      modal.restore();
    }
  }

  public updateUserWindowPreferenceForModal(modalId: string) {
    const modal = this.modals.find((o) => o.modalId === modalId);
    if (modal) {
      const handler = this.getModalLayoutPersistHandler();
      handler.storeUserWindowPreferenceForModal(modal);
    }
  }

  public updateUserWindowPreferenceForAllModals() {
    const handler = this.getModalLayoutPersistHandler();
    this.modals.forEach((modal) => handler.storeUserWindowPreferenceForModal(modal));
  }

  public restoreAllModalsFromUserWindowPreferences(attemptCsChatRestore: boolean) {
    const handler = this.getModalLayoutPersistHandler();
    const userWindowPreferences = handler.getAllUserWindowPreferences();
    userWindowPreferences.forEach((userWindowPreference) => this.restoreModalFromUserWindowPreference(userWindowPreference, attemptCsChatRestore));
    if (!this.willCmChatBeRestoredFromUserWindowPreferences(userWindowPreferences)) {
      this.spawnAgent.spawnCmChatModal();
    }

    if (attemptCsChatRestore && !this.willCsChatBeRestoredFromUserWindowPreferences(userWindowPreferences)) {
      this.spawnAgent.spawnCsChatModal();
    }
  }

  private willCmChatBeRestoredFromUserWindowPreferences(userWindowPreferences: UserWindowPreference[]): boolean {
    return userWindowPreferences.some((userWindowPreference) => this.isCmChatUserWindowPreference(userWindowPreference));
  }

  private willCsChatBeRestoredFromUserWindowPreferences(userWindowPreferences: UserWindowPreference[]): boolean {
    return userWindowPreferences.some((userWindowPreference) => this.isCsChatUserWindowPreference(userWindowPreference));
  }

  private isCmChatUserWindowPreference(userWindowPreference: UserWindowPreference): boolean {
    const config = this.spawnAgent.getConfiguration();
    const cmChatRoomId = config ? config.cmChatRoomId : 0;
    const launchCondition = userWindowPreference.launchCondition;
    return userWindowPreference.type === WindowType.Chat && launchCondition.gameName === 'cmchat' && launchCondition.id === cmChatRoomId;
  }

  private isCsChatUserWindowPreference(userWindowPreference: UserWindowPreference): boolean {
    const config = this.spawnAgent.getConfiguration();
    const csChatRoomId = config ? config.csChatRoomId : 0;
    const launchCondition = userWindowPreference.launchCondition;
    return userWindowPreference.type === WindowType.Chat && launchCondition.gameName === 'cschat' && launchCondition.id === csChatRoomId;
  }

  public restoreCmChatModalFromUserWindowPreferences() {
    const handler = this.getModalLayoutPersistHandler();
    const userWindowPreferences = handler.getAllUserWindowPreferences();
    const cmChatUserWindowPreference = userWindowPreferences.find((userWindowPreference) => this.isCmChatUserWindowPreference(userWindowPreference));
    const cmChatWindowLocation = cmChatUserWindowPreference ? cmChatUserWindowPreference.location : null;
    this.spawnAgent.spawnCmChatModal(cmChatWindowLocation);
  }

  public restoreCsChatModalFromUserWindowPreferences() {
    const handler = this.getModalLayoutPersistHandler();
    const userWindowPreferences = handler.getAllUserWindowPreferences();
    const csChatUserWindowPreference = userWindowPreferences.find((userWindowPreference) => this.isCsChatUserWindowPreference(userWindowPreference));
    const csChatWindowLocation = csChatUserWindowPreference ? csChatUserWindowPreference.location : null;
    this.spawnAgent.spawnCsChatModal(csChatWindowLocation);
  }

  public findUserWindowPreference(windowType: WindowType, modalProps?: IModalLaunchOptions): UserWindowPreference | undefined {
    const handler = this.getModalLayoutPersistHandler();
    const userWindowPreferences = handler.getAllUserWindowPreferences();
    const userWindowPreferencesPredicate = this.getUserWindowPreferencesSearchPredicate(windowType, modalProps);
    return userWindowPreferences.find(userWindowPreferencesPredicate);
  }

  private getUserWindowPreferencesSearchPredicate(windowType: WindowType, modalProps?: IModalLaunchOptions): (userWindowPreference: UserWindowPreference) => boolean {
    const validateWindowType = (userWindowPreference: UserWindowPreference) => userWindowPreference.type === windowType;

    if (windowType === WindowType.UserSettings || windowType === WindowType.PlayerNotes || windowType === WindowType.ChatContextInteraction) {
      return (userWindowPreference) => validateWindowType(userWindowPreference);
    }

    if (modalProps) {
      const validateGameLaunchCondition = (userWindowPreference: UserWindowPreference) => userWindowPreference.launchCondition.gameName === modalProps.gameName;

      if (windowType === WindowType.Chat || windowType === WindowType.Game || windowType === WindowType.IFrameGame) {
        const validateChatAndGameLaunchCondition = (userWindowPreference: UserWindowPreference) => validateGameLaunchCondition(userWindowPreference) && userWindowPreference.launchCondition.id === modalProps.id;
        return (userWindowPreference) => validateWindowType(userWindowPreference) && validateChatAndGameLaunchCondition(userWindowPreference);
      }

      if (windowType === WindowType.ArcadeGame || windowType === WindowType.GameWinners) {
        return (userWindowPreference) => validateWindowType(userWindowPreference) && validateGameLaunchCondition(userWindowPreference);
      }
    }
  }

  private restoreModalFromUserWindowPreference(userWindowPreference: UserWindowPreference, attemptCsChatRestore: boolean) {
    const location = userWindowPreference.location;

    if (this.isCmChatUserWindowPreference(userWindowPreference)) {
      this.spawnAgent.spawnCmChatModal(location);
    }
    else if (attemptCsChatRestore && this.isCsChatUserWindowPreference(userWindowPreference)) {
      this.spawnAgent.spawnCsChatModal(location);
    }
    else if (userWindowPreference.autoRestore && this.validateLaunchCondition(userWindowPreference)) {
      this.spawnModelByWindowType(userWindowPreference);
    }
  }

  private spawnModelByWindowType(userWindowPreference: UserWindowPreference): void {
    const launchCondition = userWindowPreference.launchCondition;
    const windowType = userWindowPreference.type;
    const location = userWindowPreference.location;

    switch (windowType) {
      case WindowType.Chat:
        this.spawnAgent.spawnChatModal(launchCondition.gameName, launchCondition.id, launchCondition.chatRoomName, location);
        break;
      case WindowType.Game:
      case WindowType.IFrameGame:
        this.spawnAgent.spawnGameModal(launchCondition.gameName, launchCondition.id, location);
        break;
      case WindowType.ArcadeGame:
        this.spawnAgent.spawnArcadeGameModal(launchCondition.gameName, location);
        break;
      case WindowType.GameWinners:
        this.spawnAgent.spawnWinnersModal(launchCondition.gameName, location);
        break;
      case WindowType.UserSettings:
        this.spawnAgent.spawnSettingsModal(location);
        break;
      case WindowType.PlayerNotes:
        this.spawnAgent.spawnPlayerNotesModal(location);
        break;
      default:
         // fail silently
        console.log('unknow window type found');
        break;
    }
  }

  private validateLaunchCondition(userWindowPreference: UserWindowPreference): boolean {
    const launchCondition = userWindowPreference.launchCondition;
    const windowType = userWindowPreference.type;

    if (windowType === WindowType.Chat) {
      return this.validateChatWindowLaunchCondition(launchCondition);
    }

    if (windowType === WindowType.Game || windowType === WindowType.IFrameGame) {
      return this.validateGameWindowLaunchCondition(launchCondition);
    }

    return true;
  }

  private validateChatWindowLaunchCondition(launchCondition: IWindowLaunchCondition): boolean {
    const config = this.spawnAgent.getConfiguration();
    const chatRoomConfig = config ? config.chatRoomConfiguration : null;
    if (!chatRoomConfig) {
      return false;
    }

    const chatRoomsForGame = chatRoomConfig.filter((o) => o.chatRooms && compareGameNames(o.name, launchCondition.gameName) === 0)
                                           .map((o) => o.chatRooms)
                                           .reduce((prev, current) => current, []);
    return chatRoomsForGame.some((o) => o.Id === launchCondition.id);
  }

  private validateGameWindowLaunchCondition(launchCondition: IWindowLaunchCondition): boolean {
    const config = this.spawnAgent.getConfiguration();
    const gameConfig = config ? config.gameConfiguration : null;
    if (!gameConfig) {
      return false;
    }

    const clubsForGame = gameConfig.filter((o) => o.Clubs && compareGameNames(o.Name, launchCondition.gameName) === 0)
                                   .map((o) => o.Clubs)
                                   .reduce((prev, current) => current, []);
    return clubsForGame.some((o) => o.Id === launchCondition.id);
  }

  private storeUserWindowPreferenceForModal(modal: IModal) {
    const handler = this.getModalLayoutPersistHandler();
    handler.storeUserWindowPreferenceForModal(modal);
  }

  private disableAutoRestoreUserWindowPreferenceForModal(modal: IModal): void {
    const handler = this.getModalLayoutPersistHandler();
    handler.disableAutoRestoreUserWindowPreferenceForModal(modal);
  }

  private getModalLayoutPersistHandler(): IModalLayoutStorageHandler {
    const userPreferences = this.getUserPreferences();
    return userPreferences.rememberWindowLayout ? this.defaultModalLayoutStorageHandler : this.nullModalLayoutStorageHandler;
  }

  private getUserPreferences(): UserPreferences {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    return persistedStore.Get(UserPreferences);
  }
}

export {
  MODAL_ARRANGEMENT_STEP_PIXELS,
  ModalManagerPlugin
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/plugins/modal-manager/modal-manager.ts